import React, { useState, useEffect, useCallback } from 'react';

import { PlusOutlined, EditOutlined, DeleteOutlined, InfoCircleOutlined, QuestionCircleOutlined, CalculatorOutlined } from '@ant-design/icons';
import { Button, Space, Table, Tag, message, Modal, Row, Col, Card, Descriptions, Empty, Spin, Tabs, Typography, Divider, Popover } from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

import Can from '@shared/permissions/Can';
import { rateCardService } from '@services/operator/rateCardService';

import RateCardForm from './RateCardForm';
import StoragePreviewResult from './StoragePreviewResult';

const { Text } = Typography;

const STATUS_COLORS = {
  active: 'green',
  scheduled: 'blue',
  expired: 'default',
};

// Fee config for mapping fields to labels
const FEE_CONFIG = {
  inbound: {
    fields: [
      { key: 'inbound_fee_base', label: 'fields.inbound_fee_base' },
      { key: 'inbound_fee_per_unit', label: 'fields.inbound_fee_per_unit' },
      { key: 'inbound_fee_per_box', label: 'fields.inbound_fee_per_box' },
      { key: 'inbound_fee_per_pallet', label: 'fields.inbound_fee_per_pallet' },
    ],
  },
  picking: {
    fields: [
      { key: 'picking_fee_per_item', label: 'fields.picking_fee_per_item' },
      { key: 'picking_fee_box', label: 'fields.picking_fee_box' },
      { key: 'picking_fee_pallet', label: 'fields.picking_fee_pallet' },
    ],
  },
  storage: {
    fields: [
      { key: 'price_per_pallet', label: 'fields.price_per_pallet' },
      { key: 'price_per_cuft', label: 'fields.price_per_cuft' },
      { key: 'price_per_kg', label: 'fields.price_per_kg' },
      { key: 'price_per_pcs', label: 'fields.price_per_pcs' },
    ],
  },
  other: {
    fields: [
      { key: 'order_document_fee', label: 'fields.order_document_fee' },
      { key: 'return_verify_fee', label: 'fields.return_verify_fee' },
      { key: 'return_document_fee', label: 'fields.return_document_fee' },
    ],
  },
};

const RateCardTab = ({ merchantId, editable }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [rateCards, setRateCards] = useState([]);
  const [currentCard, setCurrentCard] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [editingCard, setEditingCard] = useState(null);
  const [preview, setPreview] = useState({ result: null, error: null, loading: false });

  const prefix = 'operator.merchants.show';
  const rcPrefix = 'operator.rate_cards';

  // 對「當前生效卡」試算：用其設定對貨主當前實際庫存算當日倉租
  const handlePreviewCurrent = async () => {
    if (!currentCard) return;
    setPreview({ result: null, error: null, loading: true });
    try {
      const res = await rateCardService.preview(merchantId, {
        storage_billing_mode: currentCard.storage_billing_mode,
        price_per_pallet: currentCard.price_per_pallet,
        price_per_cuft: currentCard.price_per_cuft,
        price_per_kg: currentCard.price_per_kg,
        price_per_pcs: currentCard.price_per_pcs,
        rate_card_shelf_prices_attributes: (currentCard.shelf_prices || []).map(sp => ({
          shelf_type_id: sp.shelf_type_id,
          price: sp.price,
        })),
      });
      if (res.success) {
        setPreview({ result: res.data, error: null, loading: false });
      } else {
        setPreview({ result: null, error: t(`${prefix}.preview.error`), loading: false });
      }
    } catch (err) {
      console.error('[rate card preview] failed:', err);
      setPreview({ result: null, error: t(`${prefix}.preview.error`), loading: false });
    }
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setPreview({ result: null, error: null, loading: false });
      const [listRes, currentRes] = await Promise.all([
        rateCardService.fetchList(merchantId),
        rateCardService.getCurrent(merchantId),
      ]);
      if (listRes.success) setRateCards(listRes.data || []);
      if (currentRes.success) setCurrentCard(currentRes.data);
    } catch (error) {
      console.error('Failed to fetch rate cards:', error);
    } finally {
      setLoading(false);
    }
  }, [merchantId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreate = () => {
    const initial = currentCard ? {
      storage_billing_mode: currentCard.storage_billing_mode,
      inbound_fee_base: currentCard.inbound_fee_base,
      inbound_fee_per_pallet: currentCard.inbound_fee_per_pallet,
      inbound_fee_per_box: currentCard.inbound_fee_per_box,
      inbound_fee_per_unit: currentCard.inbound_fee_per_unit,
      picking_fee_per_item: currentCard.picking_fee_per_item,
      picking_fee_box: currentCard.picking_fee_box,
      picking_fee_pallet: currentCard.picking_fee_pallet,
      price_per_pallet: currentCard.price_per_pallet,
      price_per_cuft: currentCard.price_per_cuft,
      price_per_kg: currentCard.price_per_kg,
      price_per_pcs: currentCard.price_per_pcs,
      order_document_fee: currentCard.order_document_fee,
      return_verify_fee: currentCard.return_verify_fee,
      return_document_fee: currentCard.return_document_fee,
      shelf_prices: currentCard.shelf_prices,
    } : {
      storage_billing_mode: 'by_sku_pallet',
    };
    setEditingCard(null);
    setFormOpen(true);
    setTimeout(() => setEditingCard({ __initial: true, ...initial }), 0);
  };

  const handleEdit = (record) => {
    setEditingCard(record);
    setFormOpen(true);
  };

  const handleDelete = (record) => {
    Modal.confirm({
      title: t(`${rcPrefix}.confirm.delete_title`),
      content: t(`${rcPrefix}.confirm.delete_content`, { version: record.version }),
      okText: t('common.button.confirm'),
      cancelText: t('common.button.cancel'),
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          const res = await rateCardService.delete(merchantId, record.id);
          if (res.success) {
            message.success(t(`${rcPrefix}.messages.delete_success`));
            fetchData();
          } else {
            message.error(t(`shared.errors.${res.message}`, { defaultValue: res.message }));
          }
        } catch {
          message.error(t(`${rcPrefix}.messages.delete_failed`));
        }
      },
    });
  };

  const handleFormSubmit = async (values) => {
    try {
      setFormLoading(true);
      const isEdit = editingCard && !editingCard.__initial;
      const res = isEdit
        ? await rateCardService.update(merchantId, editingCard.id, values)
        : await rateCardService.create(merchantId, values);

      if (res.success) {
        message.success(t(`${rcPrefix}.messages.${isEdit ? 'update_success' : 'create_success'}`));
        setFormOpen(false);
        setEditingCard(null);
        fetchData();
      } else {
        message.error(t(`shared.errors.${res.message}`, { defaultValue: res.message }));
      }
    } catch {
      message.error(t(`${rcPrefix}.messages.save_failed`));
    } finally {
      setFormLoading(false);
    }
  };

  const renderDescriptions = (fields) => (
    <Descriptions column={2} bordered size="small" layout="horizontal" labelStyle={{ width: '180px', backgroundColor: '#fafafa' }}>
      {fields.map(f => (
        <Descriptions.Item key={f.key} label={t(`${prefix}.${f.label}`)}>
          {currentCard[f.key] != null ? `$${currentCard[f.key]}` : '-'}
        </Descriptions.Item>
      ))}
    </Descriptions>
  );

  const currentCardTabItems = currentCard ? [
    {
      key: 'storage',
      label: t(`${prefix}.fee_sections.storage`),
      children: (
        <div style={{ padding: '16px 0' }}>
          <Descriptions column={1} bordered size="small" labelStyle={{ width: '180px', backgroundColor: '#fafafa' }}>
            <Descriptions.Item label={t(`${prefix}.fields.storage_billing_mode`)}>
              <Popover
                title={t(`${prefix}.billing_modes.${currentCard.storage_billing_mode}`)}
                content={
                  <div style={{ maxWidth: 300 }}>
                    <p>{t(`${prefix}.billing_mode_details.${currentCard.storage_billing_mode}.desc`)}</p>
                    <p style={{ fontStyle: 'italic', fontSize: '12px', color: '#888' }}>
                      {t(`${prefix}.billing_mode_details.${currentCard.storage_billing_mode}.example`)}
                    </p>
                    {t(`${prefix}.billing_mode_details.${currentCard.storage_billing_mode}.required`) !== '無' && (
                      <div style={{ marginTop: 8, color: '#cf1322', fontSize: '12px' }}>
                        <strong>{t(`${prefix}.fields.required_field`)}:</strong> {t(`${prefix}.billing_mode_details.${currentCard.storage_billing_mode}.required`)}
                      </div>
                    )}
                  </div>
                }
              >
                <Tag color="blue" style={{ marginRight: 0, cursor: 'pointer' }}>
                  {t(`${prefix}.billing_modes.${currentCard.storage_billing_mode}`)}
                  <QuestionCircleOutlined style={{ marginLeft: 4 }} />
                </Tag>
              </Popover>
            </Descriptions.Item>
          </Descriptions>

          {currentCard.storage_billing_mode === 'by_shelf' ? (
            <div style={{ marginTop: 16 }}>
              <Divider orientation="left" style={{ margin: '0 0 12px 0', fontSize: '14px' }}>
                {t(`${prefix}.fee_sections.shelf_prices`)}
              </Divider>
              <Descriptions column={2} bordered size="small" labelStyle={{ width: '180px', backgroundColor: '#fafafa' }}>
                {currentCard.shelf_prices?.map(sp => (
                  <Descriptions.Item key={sp.id} label={sp.shelf_type_name}>
                    ${sp.price}
                  </Descriptions.Item>
                ))}
              </Descriptions>
            </div>
          ) : (
            currentCard.storage_billing_mode !== 'none' ? (
              <div style={{ marginTop: 16 }}>
                <Divider orientation="left" style={{ margin: '0 0 12px 0', fontSize: '14px' }}>
                  {t(`${prefix}.fee_sections.storage`)}
                </Divider>
                {renderDescriptions(FEE_CONFIG.storage.fields)}
              </div>
            ) : (
              <div style={{ padding: '24px 0', textAlign: 'center' }}>
                <Text type="secondary">{t(`${prefix}.billing_modes.none_desc`, { defaultValue: 'No billing for storage' })}</Text>
              </div>
            )
          )}

          {currentCard.storage_billing_mode !== 'none' && (
            <div style={{ marginTop: 16 }}>
              <Button
                type="primary"
                ghost
                icon={<CalculatorOutlined />}
                onClick={handlePreviewCurrent}
                loading={preview.loading}
              >
                {t(`${prefix}.actions.calculate`)}
              </Button>
              {preview.error && (
                <div style={{ marginTop: 8, color: '#cf1322' }}>{preview.error}</div>
              )}
              {preview.result && !preview.error && <StoragePreviewResult result={preview.result} />}
            </div>
          )}
        </div>
      )
    },
    {
      key: 'inbound',
      label: t(`${prefix}.fee_sections.inbound`),
      children: <div style={{ padding: '16px 0' }}>{renderDescriptions(FEE_CONFIG.inbound.fields)}</div>
    },
    {
      key: 'picking',
      label: t(`${prefix}.fee_sections.picking`),
      children: <div style={{ padding: '16px 0' }}>{renderDescriptions(FEE_CONFIG.picking.fields)}</div>
    },
    {
      key: 'other',
      label: t(`${prefix}.fee_sections.other`),
      children: <div style={{ padding: '16px 0' }}>{renderDescriptions(FEE_CONFIG.other.fields)}</div>
    }
  ] : [];

  const columns = [
    {
      title: t(`${rcPrefix}.fields.version`),
      dataIndex: 'version',
      key: 'version',
      width: 100,
      render: (v) => `v${v}`,
    },
    {
      title: t(`${rcPrefix}.fields.effective_from`),
      dataIndex: 'effective_from',
      key: 'effective_from',
      width: 150,
      render: (val) => val ? dayjs(val).format('YYYY-MM-DD') : '-',
    },
    {
      title: t('operator.merchants.show.fields.storage_billing_mode'),
      dataIndex: 'storage_billing_mode',
      key: 'storage_billing_mode',
      width: 180,
      render: (val) => t(`operator.merchants.show.billing_modes.${val}`, { defaultValue: val }),
    },
    {
      title: t(`${rcPrefix}.fields.created_by`),
      dataIndex: 'created_by_name',
      key: 'created_by_name',
      width: 120,
      render: (val) => val || t('common.system'),
    },
    {
      title: t(`${rcPrefix}.fields.status`),
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => (
        <Tag color={STATUS_COLORS[status] || 'default'}>
          {t(`${rcPrefix}.statuses.${status}`)}
        </Tag>
      ),
    },
    ...(editable ? [{
      title: t('common.button.action'),
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space size="small">
          {record.editable && (
            <Can requiredPermission="merchants:write">
              <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
            </Can>
          )}
          {record.deletable && (
            <Button type="link" size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record)} />
          )}
        </Space>
      ),
    }] : []),
  ];

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <Spin />
      </div>
    );
  }

  return (
    <div>
      <Card 
        size="small" 
        style={{ marginBottom: 24, borderRadius: 8 }}
        title={
          <Space>
            <InfoCircleOutlined style={{ color: '#1890ff' }} />
            <span>{t(`${rcPrefix}.current_card_title`)}</span>
            {currentCard && <Tag color="green" style={{ marginLeft: 4 }}>v{currentCard.version}</Tag>}
          </Space>
        }
        extra={
          currentCard && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {t(`${rcPrefix}.fields.effective_from`)}: {dayjs(currentCard.effective_from).format('YYYY-MM-DD')}
            </Text>
          )
        }
      >
        {currentCard ? (
          <Tabs items={currentCardTabItems} size="small" />
        ) : (
          <Empty description={t(`${rcPrefix}.no_current_card`)} />
        )}
      </Card>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h4 style={{ margin: 0 }}>{t(`${rcPrefix}.version_history`)}</h4>
        {editable && (
          <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
            {t(`${rcPrefix}.actions.create`)}
          </Button>
        )}
      </div>
      <Table
        columns={columns}
        dataSource={rateCards}
        rowKey="id"
        size="small"
        pagination={false}
      />

      <RateCardForm
        open={formOpen}
        onCancel={() => { setFormOpen(false); setEditingCard(null); }}
        onSubmit={handleFormSubmit}
        initialValues={editingCard}
        loading={formLoading}
        isEdit={editingCard && !editingCard.__initial}
        merchantId={merchantId}
      />
    </div>
  );
};

export default RateCardTab;

